import {
  addHours,
  areRangesOverlapping,
  differenceInMinutes,
  endOfDay,
  endOfHour,
  format,
  isAfter,
  isThisHour,
  startOfDay,
  startOfHour
} from 'date-fns';
import {
  IMediaItemDuration,
  IViewMode,
  IViewModeColumn,
  MediaItem,
  TimelineDataItem,
  TreeItem,
  ViewModeType
} from '../store/types';
import * as Utils from './utils';

export class HoursViewMode implements IViewMode {
  public type = ViewModeType.Months;
  public columns: IViewModeColumn[];
  constructor() {
    this.computeColumns = this.computeColumns.bind(this);
  }

  public computeColumns = (startDate: Date, endDate: Date) => {
    this.columns = [];
    startDate = startOfDay(startDate);
    endDate = endOfDay(endDate);
    let currDate = startDate;
    const width = 80;
    let alternateIndex = 0;
    let lastDay = startDate.getDate();
    while (!isAfter(currDate, endDate)) {
      const s = startOfHour(currDate);
      const e = endOfHour(currDate);
      this.columns.push({
        startDate: s,
        endDate: e,
        subtitle: format(currDate, 'DD.MM.YYYY'),
        title: format(currDate, 'HH:mm'),
        width,
        headerCellClassModifier: alternateIndex ? ' weekend' : '',
        headerTitleClassModifier: 'hours',
        headerSubtitleClassModifier: 'hours',
        contentCellClassModifier: alternateIndex ? ' weekend' : '',
        ...this.isColumnNow(currDate, width)
      });
      currDate = addHours(currDate, 1);
      if (lastDay !== currDate.getDate()) {
        alternateIndex = Math.abs(1 - alternateIndex);
        lastDay = currDate.getDate();
      }
    }
  };

  public applyToMedia = (media: MediaItem, treeItem: TreeItem): TimelineDataItem[] => {
    const daysInPlay = Utils.daysForDiffusionFlag(media.diffusionDays);
    const tDaysInPlay = Utils.daysForDiffusionFlag(treeItem.diffusionDays);

    return this.columns.map<TimelineDataItem>((c, i) => {
      // first we look at the media and how it is with respect to the column

      // check the diffusion days. if the media doesn't play in this day then there will be no duration bars in this column
      const columnDay = c.startDate.getDay();
      if (daysInPlay.indexOf(columnDay) === -1) {
        return {
          colIndex: i,
          mediaType: media.mediaType,
          durations: []
        };
      }

      const colStartTime = Utils.getTimeOnly(c.startDate);
      const colEndTime = Utils.getTimeOnly(c.endDate);

      // check if the dates of the media intersect with the column. we only compare dates here!
      const mediaDateOverlapsColumn = areRangesOverlapping(c.startDate, c.endDate, media.startDate, media.endDate);

      // check if the time range of the media intersects with the column time range. we only compare time here since the dates are equal!
      const mediaTimeColumnIntersection = Utils.getOverlappingRange(
        colStartTime,
        colEndTime,
        media.startTime,
        media.endTime
      );

      // when media has no overlap with the column whatsoever(date or time) - return no durations
      if (!mediaDateOverlapsColumn || !mediaTimeColumnIntersection.overlap) {
        return {
          colIndex: i,
          mediaType: media.mediaType,
          durations: []
        };
      }

      // we now know that there should be some duration bars in this column, but not what kind of bars.
      // the next major thing is checking with respect to the treeItem(parents valid range)

      // when the media is completely outside of the parents ranges - then just return the overlap between the col and the media and set it as outside the valid range
      if (!(treeItem.startDate && treeItem.endDate && treeItem.startTime && treeItem.endTime)) {
        // if the treeItem has null startDate and endDate that means that the media and its parent do not overlap(in another words, the media is not inside the "parent valid range")
        return this.basicMediaColumnOverlap(
          mediaTimeColumnIntersection.start,
          mediaTimeColumnIntersection.end,
          media.mediaType,
          i,
          colStartTime
        );
      }

      // we now know that the media is somehow inside the parents valid range, but we don't know how much they overlap

      // we first need to know how much of the parents range is inside the column

      // first check if the treeItem range is inside the column
      const treeItemColumnIntersection = Utils.getOverlappingRange(
        c.startDate,
        c.endDate,
        treeItem.startDate,
        treeItem.endDate
      );
      if (!treeItemColumnIntersection.overlap) {
        // if not then return the media overlap with the column
        return this.basicMediaColumnOverlap(
          mediaTimeColumnIntersection.start,
          mediaTimeColumnIntersection.end,
          media.mediaType,
          i,
          colStartTime
        );
      }

      // now check if the parents time range is inside the column
      const treeItemTimeColumnIntersection = Utils.getOverlappingRange(
        colStartTime,
        colEndTime,
        treeItem.startTime,
        treeItem.endTime
      );
      if (!treeItemTimeColumnIntersection.overlap) {
        // if not then return the media overlap with the column
        return this.basicMediaColumnOverlap(
          mediaTimeColumnIntersection.start,
          mediaTimeColumnIntersection.end,
          media.mediaType,
          i,
          colStartTime
        );
      }

      // we now know that the parents date and time ranges are also in the column
      // next step is to check how the media range and its parent ranges overlap so we know how to draw the bars

      // check if the parent diffusion days do not include this column
      const dayIsOutOfValidRange = tDaysInPlay.indexOf(columnDay) === -1;

      // we now have the media and parents intersection with the column range.
      const durations: IMediaItemDuration[] = [];

      // we perform a diff on these two. keep in mind that the parent range(the one on the TreeItem) is inside the media range due to how it is camputed!!!
      // this means that the diffs(parts that do not overlap) represent the media outside the parent range
      const diff = Utils.getDiffRange(
        mediaTimeColumnIntersection.start,
        mediaTimeColumnIntersection.end,
        treeItemTimeColumnIntersection.start,
        treeItemTimeColumnIntersection.end
      );

      // first the diff on the left of the common range
      if (diff.left) {
        durations.push({
          ...this.getDurationBarCoordinatesForRangeAndColumn(colStartTime, diff.left.start, diff.left.end),
          insideValidRange: false
        });
      }

      // now the common range
      if (diff.common) {
        durations.push({
          ...this.getDurationBarCoordinatesForRangeAndColumn(colStartTime, diff.common.start, diff.common.end),
          insideValidRange: !dayIsOutOfValidRange
        });
      }

      // now the diff on the right of the common range
      if (diff.right) {
        durations.push({
          ...this.getDurationBarCoordinatesForRangeAndColumn(colStartTime, diff.right.start, diff.right.end),
          insideValidRange: false
        });
      }
      return {
        colIndex: i,
        mediaType: media.mediaType,
        durations
      };
    });
  };

  private basicMediaColumnOverlap = (
    mediaStartTime: Date,
    mediaEndTime: Date,
    mediaType: number,
    colIndex: number,
    colStartTime: Date,
    insideValidRange = false
  ) => ({
    colIndex,
    mediaType,
    durations: [
      {
        ...this.getDurationBarCoordinatesForRangeAndColumn(colStartTime, mediaStartTime, mediaEndTime),
        insideValidRange
      }
    ]
  });

  private isColumnNow = (date: Date, width: number) => {
    const includesNow = isThisHour(date);
    if (!includesNow) {
      return { includesNow, nowPosition: -1 };
    }
    const beginningOfHour = startOfHour(new Date());
    const now = new Date();
    const nowPosition = (differenceInMinutes(now, beginningOfHour) / 60) * width; // 60 - number of minutes in an hour
    return { includesNow, nowPosition };
  };

  private getDurationBarCoordinatesForRangeAndColumn(colStartTime: Date, startDate: Date, endDate: Date) {
    const startDayDiff = differenceInMinutes(startDate, colStartTime);
    const start = startDayDiff / 60;
    const endDayDiff = isAfter(endOfHour(endDate), startOfHour(colStartTime))
      ? 60
      : differenceInMinutes(endDate, colStartTime);
    const end = endDayDiff / 60;
    return { start, end };
  }
}



// WEBPACK FOOTER //
// ./src/viewModes/HoursViewMode.ts